import React, { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Box, Typography, Chip, Tooltip } from '@mui/material';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import PersonIcon from '@mui/icons-material/Person';

const PIN_LEVEL_COLORS = {
  UNKNOWN: '#9e9e9e',
  THREE_PERCENT: '#8d6e63',
  SIX_PERCENT: '#a1887f',
  NINE_PERCENT: '#bcaaa4',
  TWELVE_PERCENT: '#78909c',
  FIFTEEN_PERCENT: '#607d8b',
  EIGHTEEN_PERCENT: '#546e7a',
  TWENTY_ONE_PERCENT: '#455a64',
  TWENTY_FIVE_PERCENT: '#37474f',
  SILVER: '#b0bec5',
  GOLD: '#ffd54f',
  PLATINUM: '#90caf9',
  RUBY: '#ef5350',
  SAPPHIRE: '#42a5f5',
  EMERALD: '#66bb6a',
  DIAMOND: '#ce93d8',
  CROWN_AMBASSADOR: '#ffb74d',
};

const TEAM_COLORS = [
  '#1976d2', '#388e3c', '#f57c00', '#7b1fa2',
  '#c62828', '#00838f', '#4527a0', '#ad1457',
];

function getTeamColor(teamName) {
  if (!teamName) return '#757575';
  let hash = 0;
  for (let i = 0; i < teamName.length; i++) {
    hash = teamName.charCodeAt(i) + ((hash << 5) - hash);
  }
  return TEAM_COLORS[Math.abs(hash) % TEAM_COLORS.length];
}

const UserNode = memo(({ data, selected }) => {
  const { user, onContextMenu } = data;
  const pinColor = PIN_LEVEL_COLORS[user.pinLevel] || PIN_LEVEL_COLORS.UNKNOWN;
  const teamColor = getTeamColor(user.teamName);

  const handleContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onContextMenu) {
      onContextMenu(e, user);
    }
  };

  return (
    <Box
      onContextMenu={handleContextMenu}
      sx={{
        minWidth: 200,
        maxWidth: 240,
        borderRadius: 2,
        overflow: 'hidden',
        border: selected ? '2px solid #667eea' : '1px solid',
        borderColor: selected ? '#667eea' : 'rgba(0,0,0,0.12)',
        bgcolor: '#fff',
        boxShadow: selected
          ? '0 0 0 2px rgba(102,126,234,0.3)'
          : '0 1px 3px rgba(0,0,0,0.12)',
        transition: 'box-shadow 0.2s, border-color 0.2s',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        },
        position: 'relative',
      }}
    >
      <Handle
        type="target"
        position={Position.Top}
        style={{
          background: '#667eea',
          width: 8,
          height: 8,
          border: '2px solid #fff',
        }}
      />

      <Box sx={{ height: 4, bgcolor: pinColor }} />

      <Box sx={{ p: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography
            variant="subtitle2"
            noWrap
            sx={{ fontWeight: 600, flex: 1, fontSize: '0.8rem' }}
          >
            {user.iboName || 'Unknown'}
          </Typography>
          {user.uplineOverride && (
            <Tooltip title="Upline manually overridden (KPI sync disabled)" arrow>
              <SwapVertIcon sx={{ fontSize: 16, color: '#f57c00' }} />
            </Tooltip>
          )}
        </Box>

        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}
        >
          IBO: {user.iboNumber}
        </Typography>

        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {user.teamName && (
            <Chip
              label={user.teamName}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                bgcolor: teamColor,
                color: '#fff',
                '& .MuiChip-label': { px: 0.8 },
              }}
            />
          )}
          {user.pinLevel && user.pinLevel !== 'UNKNOWN' && (
            <Chip
              label={user.pinLevel.replace(/_/g, ' ')}
              size="small"
              variant="outlined"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                borderColor: pinColor,
                color: pinColor,
                '& .MuiChip-label': { px: 0.8 },
              }}
            />
          )}
        </Box>
      </Box>

      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          background: '#667eea',
          width: 8,
          height: 8,
          border: '2px solid #fff',
        }}
      />
    </Box>
  );
});

UserNode.displayName = 'UserNode';

export default UserNode;
